import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Avatar, type ComboboxItem } from '@node-space/ui-components'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getCryptoCurrencies } from 'services/CurrencyService'
import { LedgerWalletType, type LedgerType } from 'types/customers'

const SUPPORTED_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD']

const buildCurrencyOption = (code: string): ComboboxItem => ({
  value: code,
  leftText: code,
  LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
})

const ALL_CURRENCIES_VALUE = ''

export const useCurrencyFilterOptions = (activeTab: LedgerType) => {
  const { t } = useTranslation()
  const isCryptoTab = activeTab === LedgerWalletType.CRYPTO

  const { data: cryptoCurrencies, isPending: isFetchingCrypto } = useQuery({
    queryKey: reactQueryKeys.cryptoCurrencies(),
    queryFn: getCryptoCurrencies,
    enabled: isCryptoTab,
    refetchOnReconnect: false,
  })

  const currencyOptions: ComboboxItem[] = useMemo(() => {
    const allCurrenciesOption: ComboboxItem = {
      value: ALL_CURRENCIES_VALUE,
      leftText: t('customers.allCurrencies'),
    }

    if (!isCryptoTab) {
      return [allCurrenciesOption, ...SUPPORTED_FIAT_CURRENCIES.map(buildCurrencyOption)]
    }

    const cryptoOptions = (cryptoCurrencies ?? [])
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code))
      .map(currency => buildCurrencyOption(currency.code))

    return [allCurrenciesOption, ...cryptoOptions]
  }, [isCryptoTab, cryptoCurrencies, t])

  const isFetchingCurrencies = useMemo(
    () => isCryptoTab && isFetchingCrypto,
    [isCryptoTab, isFetchingCrypto]
  )

  return { currencyOptions, isFetchingCurrencies }
}
